import type { Permission } from '@contract-analyzer/api-client';
import type { ReactNode } from 'react';

import { Navigate, Outlet } from 'react-router';

import { useAuth } from '@/hooks/use-auth';

export interface PermissionGuardProps {
  readonly permission?: Permission | readonly Permission[];
  readonly requireSuperadmin?: boolean;
  readonly fallback?: ReactNode;
  readonly redirectTo?: string;
}

export function PermissionGuard({
  permission,
  requireSuperadmin = false,
  fallback,
  redirectTo = '/',
}: PermissionGuardProps) {
  const { hasPermission, user } = useAuth();

  const hasRequiredPermissions =
    permission === undefined
      ? true
      : typeof permission === 'string'
        ? hasPermission(permission)
        : permission.every((permissionName) => hasPermission(permissionName));

  const isAuthorized =
    user !== null && (!requireSuperadmin || user.is_superadmin) && hasRequiredPermissions;

  if (!isAuthorized) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }

    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
